import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, Output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ArrowLeft01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons/index';
import { LucideChevronLeft, LucideChevronRight } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { AutocompleteSearchOption } from '../autocomplete-search/sg-autocomplete-search.component';

export interface PaginationEvent {
  currentPage: number;
  pageSize: number;
  startIndex: number;
  endIndex: number;
}

export interface PageSizeOption {
  value: number;
  label: string;
}

/** Default rows-per-page for every paginated table. */
export const DEFAULT_PAGE_SIZE = 25;

export const DEFAULT_PAGE_SIZE_OPTIONS: PageSizeOption[] = [
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 200, label: '200' },
];

/** A gap in the page list, rendered as an ellipsis. */
export const PAGE_GAP = 'gap';

export type PageItem = number | typeof PAGE_GAP;

/** Page buttons shown before the list collapses into ellipses. */
const MAX_VISIBLE_PAGES = 7;

@Component({
  selector: 'ptl-pagination-footer',
  templateUrl: './pagination-footer.component.html',
  styleUrls: ['./pagination-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationFooterComponent {
  totalItems = input<number>(0);
  pageSize = input<number>(DEFAULT_PAGE_SIZE);
  currentPage = input<number>(1);
  flat = input<boolean>(false);
  pageSizeOptions = input<PageSizeOption[]>(DEFAULT_PAGE_SIZE_OPTIONS);
  /** Blocks every control while a page is being fetched, so a second click cannot race the first. */
  disabled = input<boolean>(false);

  @Output() pageChange = new EventEmitter<PaginationEvent>();
  @Output() pageSizeChange = new EventEmitter<number>();

  protected readonly ArrowLeft01Icon = ArrowLeft01Icon;
  protected readonly ArrowRight01Icon = ArrowRight01Icon;

  effectivePageSizeOptions = computed<PageSizeOption[]>(() => {
    const options = this.pageSizeOptions();
    const size = this.pageSize();
    if (options.some((o) => o.value === size)) {
      return options;
    }
    return [...options, { value: size, label: `${size}` }].sort((a, b) => a.value - b.value);
  });

  pageSizeMenuOptions = computed<AutocompleteSearchOption<number>[]>(() =>
    this.effectivePageSizeOptions().map((option) => ({
      value: option.value,
      label: option.label,
      isDefault: option.value === DEFAULT_PAGE_SIZE,
    })),
  );

  totalPages = computed(() => Math.ceil(this.totalItems() / this.pageSize()));

  /**
   * First, last and the pages around the current one, with gaps in between —
   * so a jump across a long list is one click instead of a dozen.
   */
  pageItems = computed<PageItem[]>(() => {
    const total = this.totalPages();
    const current = this.currentPage();

    if (total <= 1) {
      return [];
    }
    if (total <= MAX_VISIBLE_PAGES) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const items: PageItem[] = [1];
    const from = Math.max(2, Math.min(current - 1, total - 3));
    const to = Math.min(total - 1, Math.max(current + 1, 4));

    if (from > 2) {
      items.push(PAGE_GAP);
    }
    for (let page = from; page <= to; page++) {
      items.push(page);
    }
    if (to < total - 1) {
      items.push(PAGE_GAP);
    }
    items.push(total);

    return items;
  });

  private readonly transloco = inject(TranslocoService);
  private readonly activeLang = toSignal(this.transloco.langChanges$, { initialValue: this.transloco.getActiveLang() });

  /** Arabic locales default to Arabic-Indic digits, which would clash with the Latin ones everywhere else. */
  private numberFormat = computed(() => {
    const lang = this.activeLang();
    try {
      return new Intl.NumberFormat(lang?.startsWith('ar') ? 'ar-u-nu-latn' : lang);
    } catch {
      return new Intl.NumberFormat();
    }
  });

  formattedStart = computed(() => this.numberFormat().format(this.startItem()));
  formattedEnd = computed(() => this.numberFormat().format(this.endItem()));
  formattedTotal = computed(() => this.numberFormat().format(this.totalItems()));

  startItem = computed(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  endItem = computed(() => {
    const end = this.currentPage() * this.pageSize();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  onPageSizeChange(newPageSize: number): void {
    if (this.disabled()) {
      return;
    }
    this.pageSizeChange.emit(newPageSize);
  }

  onPageSelect(page: PageItem): void {
    if (page === PAGE_GAP || this.disabled() || page === this.currentPage()) {
      return;
    }
    this.emitPageChange(page, this.pageSize());
  }

  onPreviousPage(): void {
    if (!this.disabled() && this.currentPage() > 1) {
      const newPage = this.currentPage() - 1;
      this.emitPageChange(newPage, this.pageSize());
    }
  }

  onNextPage(): void {
    if (!this.disabled() && this.currentPage() < this.totalPages()) {
      const newPage = this.currentPage() + 1;
      this.emitPageChange(newPage, this.pageSize());
    }
  }

  private emitPageChange(currentPage: number, pageSize: number): void {
    const event: PaginationEvent = {
      currentPage,
      pageSize,
      startIndex: (currentPage - 1) * pageSize,
      endIndex: Math.min(currentPage * pageSize - 1, this.totalItems() - 1),
    };
    this.pageChange.emit(event);
  }

  protected readonly LucideChevronLeft = LucideChevronLeft;
  protected readonly LucideChevronRight = LucideChevronRight;
  protected readonly PAGE_GAP = PAGE_GAP;
}
